import { AlertTriangle, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import type { MerchantRow } from '../../api/types';
import {
  formatLicenseDate,
  licenseExpiryAlert,
} from '../../utils/licenseExpiry';
import {
  merchantCorporateStructure,
  merchantDailyLimit,
  merchantRollingReserve,
} from '../../utils/merchantDefaults';
import { shouldMaskPii } from '../../utils/roles';
import type { EmployeeRole } from '../../types/auth';
import { maskString } from '../../utils/piiDisplay';

type MerchantOverviewBlocksProps = {
  merchant: MerchantRow;
  balance: string | null;
  currency: string;
  role: EmployeeRole | null;
  canEditAccount: boolean;
  onEditAccount: () => void;
  canEditLegal: boolean;
  onEditLegal: () => void;
  canEditRisk: boolean;
  onEditRisk: () => void;
  canEditTechnical: boolean;
  onEditTechnical: () => void;
  canEditSettlement: boolean;
  onEditSettlement: () => void;
};

function ProfileBlock({
  title,
  children,
  onEdit,
  canEdit,
}: {
  title: string;
  children: ReactNode;
  onEdit?: () => void;
  canEdit?: boolean;
}) {
  return (
    <section className="profile-block">
      <div className="profile-block-header">
        <h3 className="profile-block-title">{title}</h3>
        {canEdit && onEdit ? (
          <button type="button" className="btn-outline btn-sm" onClick={onEdit}>
            <Pencil size={14} aria-hidden /> Edit
          </button>
        ) : null}
      </div>
      <div className="profile-grid">{children}</div>
    </section>
  );
}

function Field({ label, value, warn }: { label: string; value: ReactNode; warn?: boolean }) {
  return (
    <div className="profile-field">
      <span className="profile-field-label">{label}</span>
      <span className={`profile-field-value${warn ? ' profile-field-warn' : ''}`}>{value}</span>
    </div>
  );
}

export function MerchantOverviewBlocks({
  merchant,
  balance,
  currency,
  role,
  canEditAccount,
  onEditAccount,
  canEditLegal,
  onEditLegal,
  canEditRisk,
  onEditRisk,
  canEditTechnical,
  onEditTechnical,
  canEditSettlement,
  onEditSettlement,
}: MerchantOverviewBlocksProps) {
  const licenseAlert = licenseExpiryAlert(merchant.license_expiry_date);
  const mask = shouldMaskPii(role);
  const wallet = mask ? maskString(merchant.solana_wallet, 6, 4) : merchant.solana_wallet;
  const walletPending = merchant.solana_wallet?.startsWith('pending_');
  const iban = mask ? maskString(merchant.bank_iban, 4, 2) : merchant.bank_iban || '—';

  return (
    <>
      <ProfileBlock title="Account summary" onEdit={onEditAccount} canEdit={canEditAccount}>
        <Field label="Merchant ID" value={<span className="cell-mono">{merchant.id}</span>} />
        <Field label="Email" value={merchant.email || '—'} />
        <Field
          label="Current balance"
          value={
            <span className="profile-balance">
              {balance ?? '—'} {currency}
            </span>
          }
        />
        <Field
          label="Commission (fee_percent)"
          value={merchant.fee_percent != null ? `${merchant.fee_percent}%` : '—'}
        />
        <Field label="Registered" value={new Date(merchant.created_at).toLocaleString()} />
        <Field
          label="Settlement wallet"
          value={
            walletPending ? (
              <span className="cell-muted">Not configured</span>
            ) : (
              <span className="cell-mono">{wallet}</span>
            )
          }
        />
        <Field label="IBAN" value={<span className="cell-mono">{iban}</span>} />
      </ProfileBlock>

      <ProfileBlock title="On-chain settlement" onEdit={onEditSettlement} canEdit={canEditSettlement}>
        <Field
          label="Status"
          value={walletPending ? 'Pending KYB / setup' : 'Configured for process_payment'}
        />
        <Field
          label="Last wallet update"
          value={
            merchant.solana_wallet_updated_at
              ? `${new Date(merchant.solana_wallet_updated_at).toLocaleString()}${merchant.solana_wallet_updated_by ? ` · ${merchant.solana_wallet_updated_by}` : ''}`
              : '—'
          }
        />
        <Field label="Split model" value="98.5% merchant · 1.4% platform · 0.1% gas (on-chain)" />
      </ProfileBlock>

      <ProfileBlock title="Legal & compliance" onEdit={onEditLegal} canEdit={canEditLegal}>
        <Field
          label="Trade License Number"
          value={merchant.trade_license_number?.trim() || 'Not on file'}
        />
        <Field
          label="License Expiry Date"
          warn={licenseAlert === 'expired' || licenseAlert === 'soon'}
          value={
            <span className="license-expiry-wrap">
              {formatLicenseDate(merchant.license_expiry_date)}
              {licenseAlert === 'expired' ? (
                <span className="badge badge-blocked license-badge">
                  <AlertTriangle size={12} aria-hidden /> Expired
                </span>
              ) : null}
              {licenseAlert === 'soon' ? (
                <span className="badge badge-pending license-badge">
                  <AlertTriangle size={12} aria-hidden /> Expiring soon
                </span>
              ) : null}
            </span>
          }
        />
        <Field label="Corporate Structure" value={merchantCorporateStructure(merchant)} />
      </ProfileBlock>

      <ProfileBlock title="Risk & limits" onEdit={onEditRisk} canEdit={canEditRisk}>
        <Field label="Rolling Reserve %" value={`${merchantRollingReserve(merchant)}%`} />
        <Field label="Daily Limit" value={`${merchantDailyLimit(merchant).toLocaleString()} AED`} />
      </ProfileBlock>

      <ProfileBlock title="Technical" onEdit={onEditTechnical} canEdit={canEditTechnical}>
        <Field
          label="Webhook URL"
          value={
            merchant.webhook_url ? (
              <span className="cell-mono profile-webhook">{merchant.webhook_url}</span>
            ) : (
              <span className="cell-muted">Not configured</span>
            )
          }
        />
      </ProfileBlock>
    </>
  );
}
